/**
 * Basal body temperature helpers. Temperatures are stored in °F in `observations.temperature_f`
 * (Postgres `numeric(5,2)`), so entry, storage and display all carry up to two decimal places —
 * a thermometer reading of 96.67 must not be flattened to 96.7 anywhere on the way in or out.
 */

/** Plausible BBT range in °F; values outside are clamped on entry rather than rejected. */
export const TEMPERATURE_F_MIN = 95;
export const TEMPERATURE_F_MAX = 110;

/** Step used by numeric temperature inputs (two decimal places). */
export const TEMPERATURE_STEP = 0.01;

/** Round to two decimal places, avoiding the usual binary-float drift (e.g. 1.005 → 1.01). */
export const roundTemperature = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

/**
 * Normalize a free-text temperature entry for persistence: trim, parse, clamp to the plausible
 * range and round to two decimals. Returns "" for blank or unparseable input so callers can
 * treat that as "no temperature logged".
 *
 * The result is a string with as many decimals as the value needs (max two), so "96.7" stays
 * "96.7" and "96.67" stays "96.67".
 */
export const normalizeTemperatureInput = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";

  const parsed = Number.parseFloat(trimmed);
  if (!Number.isFinite(parsed)) return "";

  const clamped = Math.min(TEMPERATURE_F_MAX, Math.max(TEMPERATURE_F_MIN, parsed));
  return formatTemperature(clamped);
};

/**
 * Format a temperature for display with one or two decimal places: the value is rounded to two
 * decimals, then a trailing hundredths zero is dropped so legacy one-decimal readings render as
 * they were entered ("97.8", not "97.80") while "96.67" keeps its precision. Whole numbers keep
 * one decimal ("97.0") so a temperature never reads like a bare integer.
 *
 * Pass `unit` to append a unit suffix, e.g. formatTemperature(96.67, "°F") → "96.67°F".
 */
export const formatTemperature = (value: number, unit = ""): string => {
  const twoDecimals = roundTemperature(value).toFixed(2);
  const text = twoDecimals.endsWith("0") ? twoDecimals.slice(0, -1) : twoDecimals;
  return `${text}${unit}`;
};
